import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import api, { getApiError } from '../lib/axios';
import { Gavel, Plus, Loader2, ArrowRight, Tag, Clock, Check } from 'lucide-react';
import type { Campaign, CampaignStatus } from '../types';

const STATUS_STYLES: Record<CampaignStatus, string> = {
  draft:     'bg-slate-100 text-slate-700 border-slate-200',
  open:      'bg-blue-50 text-blue-700 border-blue-200',
  awarded:   'bg-emerald-50 text-emerald-700 border-emerald-200',
  closed:    'bg-slate-100 text-slate-500 border-slate-200',
  completed: 'bg-teal-50 text-teal-700 border-teal-200',
  cancelled: 'bg-rose-50 text-rose-700 border-rose-200',
};

const STATUS_ORDER: CampaignStatus[] = ['open', 'awarded', 'draft', 'completed', 'closed', 'cancelled'];

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<CampaignStatus | 'all'>('all');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = statusFilter === 'all' ? '' : `?status=${statusFilter}`;
        const res = await api.get(`/api/campaigns/mine${params}`);
        setCampaigns(res.data.campaigns);
        setError(null);
      } catch (err) {
        setError(getApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [statusFilter]);

  const grouped: Record<string, Campaign[]> = {};
  for (const c of campaigns) {
    grouped[c.status] ??= [];
    grouped[c.status].push(c);
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <header className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900 flex items-center gap-2">
              <Gavel className="w-6 h-6 text-brand-600" /> Campaigns
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Post what you need. Marketplaces bid competitively. You pick the winner.
            </p>
          </div>
          <Link
            to="/campaigns/new"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-xl shadow-sm shadow-brand-500/20 transition-colors"
          >
            <Plus className="w-4 h-4" /> New campaign
          </Link>
        </header>

        <div className="flex flex-wrap gap-2 mb-6">
          <StatusFilter value={statusFilter} onChange={setStatusFilter} label="all" />
          {STATUS_ORDER.map(s => (
            <StatusFilter key={s} value={statusFilter} onChange={setStatusFilter} label={s} />
          ))}
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-700">{error}</div>
        )}

        {loading ? (
          <div className="p-12 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
        ) : campaigns.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-3">
            {campaigns.map(c => <CampaignRow key={c.id} c={c} />)}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function StatusFilter({
  value, onChange, label,
}: { value: CampaignStatus | 'all'; onChange: (v: CampaignStatus | 'all') => void; label: string }) {
  const active = value === label;
  return (
    <button
      onClick={() => onChange(label as CampaignStatus | 'all')}
      className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
        active ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-slate-600 border-slate-200 hover:border-brand-300'
      }`}
    >
      {label}
    </button>
  );
}

function CampaignRow({ c }: { c: Campaign }) {
  const bidSummary = c.status === 'open' && c.bid_count === 0
    ? 'No bids yet'
    : c.status === 'open'
      ? `${c.bid_count} bid${c.bid_count === 1 ? '' : 's'}`
      : c.status === 'awarded'
        ? `${c.accepted_bid_count}/${c.links_needed} accepted · ${c.bid_count} total bids`
        : `${c.bid_count} bid${c.bid_count === 1 ? '' : 's'}`;

  return (
    <Link to={`/campaigns/${c.id}`} className="block bg-white border border-slate-200 hover:border-brand-300 hover:shadow-card-hover rounded-2xl p-5 transition-all">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${STATUS_STYLES[c.status]}`}>
              {c.status}
            </span>
            <span className="text-xs text-slate-500">{new Date(c.created_at).toLocaleDateString()}</span>
          </div>
          <h3 className="text-base font-bold text-slate-900 truncate">{c.title}</h3>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600 mt-2">
            {c.target_keyword && <span className="inline-flex items-center gap-1"><Tag className="w-3 h-3" /> {c.target_keyword}</span>}
            {c.min_dr != null && <span>DR ≥ {c.min_dr}</span>}
            {c.country && <span>{c.country}</span>}
            <span>≤ ${c.budget_max_per_link}/link · {c.links_needed} needed</span>
            <span className="inline-flex items-center gap-1 text-brand-600 font-semibold">
              <Clock className="w-3 h-3" /> {bidSummary}
            </span>
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-slate-300 flex-shrink-0 mt-2" />
      </div>
    </Link>
  );
}

function EmptyState() {
  return (
    <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center">
      <div className="w-14 h-14 rounded-2xl bg-brand-50 border border-brand-100 flex items-center justify-center mx-auto mb-4">
        <Gavel className="w-6 h-6 text-brand-600" />
      </div>
      <h2 className="text-xl font-bold text-slate-900 mb-2">No campaigns yet</h2>
      <p className="text-sm text-slate-500 max-w-md mx-auto mb-5">
        Create a campaign with your requirements and budget. Marketplaces bid
        competitively against each other for your business. You pick the winner
        and get click-through tracking.
      </p>
      <Link
        to="/campaigns/new"
        className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-xl transition-colors"
      >
        <Plus className="w-4 h-4" /> Create your first campaign
      </Link>
      <div className="mt-8 pt-6 border-t border-slate-100 grid grid-cols-3 gap-4 text-xs text-slate-500">
        <StepHint n={1} text="Post requirements + budget" />
        <StepHint n={2} text="Sellers bid competitively" />
        <StepHint n={3} text="You pick, we track" />
      </div>
    </div>
  );
}

function StepHint({ n, text }: { n: number; text: string }) {
  return (
    <div>
      <div className="w-7 h-7 rounded-full bg-brand-100 text-brand-700 text-xs font-bold flex items-center justify-center mx-auto mb-1.5">
        {n}
      </div>
      {text}
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _Check = Check; // keep import used for typecheck
